<template>
  <div>
    <OverviewComponent
      icon="mdi-hammer-wrench"
      :subtitle="subtitle"
      :headers="headers"
      :data="data"
      :loading="loading"
      :totalPages="totalPages"
      :totalItemCount="totalItemCount"
      deadlineKey="deadLine"
      :defaultSortBy="defaultSortBy"
      :defaultSortDesc="defaultSortDesc"
      @setHeader="setHeader"
      @fetchData="fetchData"
      @setSubtitle="setSubtitle"
      @setColor="setColor"
      @click="click"
    >
      <template v-slot:chips="{ item }">
        <div v-for="(chip, index) in getChips(item)" :key="index">
          <v-chip class="mr-2" :color="chip.color" small>
            <v-icon v-if="chip.isCompleted" color="white" left small>
              mdi-check-circle
            </v-icon>
            {{ chip.title }}
          </v-chip>
        </div>
      </template>

      <template v-slot:underLeft="{ item }">
        {{ item.projectName }}
        {{ item.projectName && item.houseNum ? "-" : "" }}
        {{ item.houseNum }}
      </template>
      <template v-slot:item.activitiesCount="{ item }">
        <v-chip class="mr-2" :color="getActivitiesColor(item)" small>
          {{ item.activitiesCompletedCount }}/{{ item.activitiesCount }}
          Fullført
        </v-chip>
      </template>

      <!-- Custom filter -->
      <template v-slot:filter.projectName="{ head, isTable }">
        <v-autocomplete
          v-if="!head.removeFilter"
          :class="{ 'mb-0': isTable }"
          v-model="head.filterValues"
          :items="isTrysilhus ? projects : projects"
          :filled="!isTable"
          :outlined="isTable"
          :dense="isTable"
          :label="isTable ? '' : head.text"
          :placeholder="isTable ? 'Søk...' : ''"
          item-text="name"
          item-value="id"
          clearable
          :disabled="head.disabled"
          hide-details="auto"
          menu-props="auto"
          multiple
        >
          <template v-slot:selection="{ item, index }">
            <span v-if="index === 0">{{ item.name }}</span>
            <span v-if="index === 1" class="grey--text text-caption">
              &nbsp;(+{{ head.filterValues.length - 1 }} andre)
            </span>
          </template>
        </v-autocomplete>
      </template>
      <!-- <template v-slot:filter.name="{ head }"> {{ head.value }} </template> -->
      <template v-slot:filter.assignedTo="{ head, isTable }">
        <v-autocomplete
          v-if="!head.removeFilter"
          :class="{ 'mb-0': isTable }"
          v-model="head.filterValues"
          :items="isTrysilhus ? contactPersons : contactPersonContact"
          :filled="!isTable"
          :outlined="isTable"
          :dense="isTable"
          :label="isTable ? '' : 'Ansvarlig'"
          :placeholder="isTable ? 'Ansvarlig' : ''"
          item-text="name"
          item-value="id"
          clearable
          :disabled="head.disabled"
          hide-details="auto"
          multiple
        >
          <template v-slot:selection="{ item, disabled, index }">
            <div
              v-if="typeof head.filterValues === 'object'"
              class="v-select__selection v-select__selection--comma"
            >
              {{
                head.filterValues && head.filterValues.length === 1
                  ? item.name
                  : index === 1
                  ? `${head.filterValues.length} valgt`
                  : ""
              }}
            </div>
            <div
              v-else
              class="v-select__selection v-select__selection--comma"
              :class="{ 'v-select__selection--disabled': disabled }"
            >
              {{ item.name }}
            </div>
          </template>

          <template v-slot:item="{ active, item, attrs, on }">
            <v-list-item
              v-on="on"
              v-bind="attrs"
              #default="{ active }"
              :disabled="
                !isTrysilhus &&
                attrs.inputValue &&
                head.filterValues.length === 1
              "
            >
              <v-list-item-action>
                <v-checkbox :input-value="active"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </template>
    </OverviewComponent>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";
import { HeaderObject } from "@/types/tableInterface";
import taskApi from "@/api/taskApi";
import moment from "moment";

import OverviewComponent from "@/components/shared/overview/OverviewComponent.vue";

export default Vue.extend({
  data: () => ({
    data: [] as Array<unknown>,
    headers: [] as Array<HeaderObject>,
    subtitle: "",
    loading: true,
    totalPages: 0,
    totalItemCount: -1,
    defaultSortBy: ["createdDate"],
    defaultSortDesc: [true],
    contactPersons: [],
    users: [],
    contactPersonContact: [],
    projects: [],
  }),
  async created() {
    await this.getFilterData();
  },
  methods: {
    async fetchData({ data, bottomOfPage }) {
      this.loading = true;

      await taskApi
        .getTask(data)
        .then((response) => {
          if (bottomOfPage) {
            this.data = [...this.data, ...response.items];
          } else {
            this.data = response.items;
          }
          this.totalPages = response.totalPages;
          this.totalItemCount = response.totalItemCount;

          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    async getFilterData() {
      await taskApi.getFilteroptions().then((response) => {
        this.contactPersons = response.contactPersons;
        this.users = response.users;
        this.contactPersonContact = response.contactPersonContact;
        this.projects = response.projects;
      });
    },
    setSubtitle() {
      this.subtitle =
        this.showAllActions && this.isTrysilhus
          ? "Alle egenkontroller"
          : "Dine egenkontroller";
    },
    clickCard(item: any) {
      this.$router.push({ name: "Action", params: { id: item.id } });
    },
    getChips(item: any) {
      var data = [
        {
          title: `${item.activitiesCompletedCount}/${item.activitiesCount} Fullført`,
          color: item.color,
          isCompleted: item.isCompleted,
        },
      ];

      if (item.completedDate) {
        data.push({
          title: item.tradeCodeName,
          color: "",
          isCompleted: false,
        });
      }
      return data;
    },
    getActivitiesColor(item: any) {
      if (item.activitiesCompletedCount === item.activitiesCount) {
        return "success";
      } else if (
        item.activitiesCompletedCount > 0 &&
        item.activitiesCompletedCount < item.activitiesCount
      ) {
        return "warning";
      } else {
        return "error";
      }
    },
    setColor(item: any) {
      if (item.completedDate) {
        this.$set(item, "color", "success");
      } else if (
        item.activitiesCompletedCount === 0 &&
        item.activitiesCount > 0
      ) {
        if (moment().isBefore(item.deadLine)) {
          this.$set(item, "color", "info");
        } else {
          this.$set(item, "color", "error");
        }
      } else {
        this.$set(item, "color", "warning");
      }
    },
    setHeader() {
      this.headers = [
        {
          text: "Type",
          value: "taskType",
          type: "string",
          show: false,
          filterValues: "2",
          filterClause: 0,
          filterName: "taskType",
          removeFilter: true,
        },
        {
          text: "Id",
          value: "id",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "id",
          sortIndex: 0,
        },
        {
          text: "Tittel",
          value: "name",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "name",
          sortIndex: 1,
        },
        {
          text: "Status",
          value: "activitiesCount",
          type: "select",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "activitiesCount",
          sortIndex: 2,
          removeFilter: true,
        },
        {
          text: "Ferdigmeld",
          value: "isCompleted",
          type: "boolean",
          show: true,
          filterValues: "false",
          filterClause: 0,
          filterName: "isCompleted",
          sortIndex: 2,
        },
        {
          text: "Frist",
          value: "deadLine",
          type: "date",
          show: true,
          filterValues: null,
          filterClause: 0,
          filterName: "deadLine",
          sortIndex: 4,
          dateType: "DD.MM.YYYY",
        },
        {
          text: "Prosjekt",
          value: "projectName",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "projectId",
          sortIndex: 5,
        },
        {
          text: "Bygg",
          value: "houseNum",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "houseNum",
          sortIndex: 5,
        },
        {
          text: "Ansvarlig",
          value: "assignedTo",
          type: "string",
          show: true,
          filterValues: this.showAllActions
            ? !this.isTrysilhus
              ? this.contactPersonContactIds
              : ""
            : this.profile.id,
          filterClause: 0,
          filterName: "assignedToId",
          sortIndex: 6,
          disabled: this.showAllActions ? false : true,
          isAssignedTo: true,
        },
        // {
        //   text: "Ansvarlig",
        //   value: "assignedTo",
        //   type: "string",
        //   show: false,
        //   filterValues: this.showAllActions
        //     ? !this.isTrysilhus
        //       ? this.contactPersonContactIds
        //       : ""
        //     : this.profile.id,
        //   filterClause: 0,
        //   filterName: "assignedToId",
        //   removeFilter: true,
        // },
        {
          text: "Fagområde",
          value: "tradeCodeName",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "tradeCodeName",
        },
        {
          text: "Opprettet",
          value: "createdDate",
          type: "date",
          show: true,
          filterValues: null,
          filterClause: 0,
          filterName: "createdDate",
          dateType: "DD.MM.YYYY",
        },
      ];
    },
    click(item) {
      this.$router.push({ name: "Checkup", params: { id: item.id } });
    },
  },

  computed: {
    ...mapGetters({
      isTrysilhus: "shared/isTrysilhus",
      profile: "shared/profile",
      showAll: "tables/showAll",
      contactPersonContactIds: "shared/contactPersonContactIds",
    }),
    showAllActions(): boolean {
      return this.showAll.value;
    },
  },
  components: {
    OverviewComponent,
  },
});
</script>
